import React, { useEffect, useState, useCallback, useRef } from 'react';
import { EMPTY_FORM, FIELD_ORDER, FIELD_LABELS } from '../constants';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import {
  getAllDoctorsAdmin,
  createDoctorAdmin,
  updateDoctorAdmin,
  deactivateDoctorAdmin,
  reactivateDoctorAdmin,
  LANGUAGE_OPTIONS,
} from '../services/adminApi';
import { getDepartments } from '../services/doctorsApi';
import api from '../services/api';

const Toast = ({ message, type, visible }) => (
  <div style={{
    position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
    padding: '10px 18px', borderRadius: 10, fontSize: 13, fontWeight: 500,
    background: type === 'success' ? '#eaf3de' : type === 'error' ? '#fcebeb' : '#faeeda',
    color: type === 'success' ? '#3b6d11' : type === 'error' ? '#a32d2d' : '#854f0b',
    border: `0.5px solid ${type === 'success' ? '#97c459' : type === 'error' ? '#f09595' : '#fac775'}`,
    boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0)' : 'translateY(8px)',
    transition: 'opacity 0.2s, transform 0.2s',
    pointerEvents: 'none',
    maxWidth: 360,
  }}>
    {message}
  </div>
);

// ---------------------------------------------------------------------------
// Confirm deactivate / reactivate modal
// ---------------------------------------------------------------------------
const ConfirmModal = ({ doctor, action, onConfirm, onCancel, loading }) => {
  const isDeactivate = action === 'deactivate';
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="card-elevated modal-inner" onClick={e => e.stopPropagation()} style={{ maxWidth: 380 }}>
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <h3 className="modal-title" style={{ fontSize: 17 }}>
            {isDeactivate ? 'Deactivate doctor?' : 'Reactivate doctor?'}
          </h3>
          <p className="modal-sub" style={{ marginTop: 6 }}>
            <strong style={{ color: '#1e1b4b' }}>{doctor?.name}</strong>{' '}
            {isDeactivate
              ? 'will no longer be able to log in or appear in doctor listings. This can be reversed later by an admin.'
              : 'will regain login access and reappear in doctor listings.'}
          </p>
        </div>
        <div className="modal-actions">
          <button onClick={onCancel} className="btn-outline" style={{ flex: 1, color: '#6b7280' }}>
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            style={{
              flex: 2, padding: '12px', fontSize: 14, fontWeight: 600,
              borderRadius: 10, cursor: loading ? 'not-allowed' : 'pointer',
              border: 'none',
              background: isDeactivate ? '#dc2626' : '#059669',
              color: '#fff',
              opacity: loading ? 0.6 : 1, transition: 'opacity .15s',
              fontFamily: 'DM Sans, sans-serif',
            }}>
            {loading
              ? (isDeactivate ? 'Deactivating…' : 'Reactivating…')
              : (isDeactivate ? 'Deactivate' : 'Reactivate')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Toggle pill (reused for availableOnline / availableOffline)
// ---------------------------------------------------------------------------
const TogglePill = ({ checked, onChange, label, color = '#4f46e5', bg = '#eef2ff' }) => (
  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
    <div
      onClick={() => onChange(!checked)}
      style={{
        width: 38, height: 22, borderRadius: 11,
        background: checked ? color : '#d1d5db',
        position: 'relative', transition: 'background .2s', cursor: 'pointer', flexShrink: 0,
      }}
    >
      <div style={{
        position: 'absolute', top: 3, left: checked ? 18 : 3,
        width: 16, height: 16, borderRadius: '50%', background: '#fff',
        transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </div>
    <span style={{ fontSize: 13, fontWeight: 500, fontFamily: 'DM Sans, sans-serif', color: checked ? color : '#6b7280' }}>
      {label}
    </span>
    {checked && (
      <span style={{ fontSize: 11, fontWeight: 600, padding: '1px 7px', borderRadius: 9999, background: bg, color, fontFamily: 'DM Sans, sans-serif' }}>
        ON
      </span>
    )}
  </label>
);

// ---------------------------------------------------------------------------
// Doctor create/edit modal
// ---------------------------------------------------------------------------
const DoctorModal = ({ doctor, departments, hospitals, onSave, onClose }) => {
  const isEdit = !!doctor?.id;
  const [form, setForm] = useState(
    isEdit
      ? {
          phoneNumber: doctor.phoneNumber || '',
          name: doctor.name || '',
          department: doctor.department || '',
          qualifications: doctor.qualifications || '',
          experience: doctor.yearsExperience?.toString() || '',
          email: doctor.email || '',
          photoUrl: doctor.photoUrl || '',
          languages: Array.isArray(doctor.languages) ? doctor.languages : [],
          about: doctor.about || '',
          onlineFee: doctor.onlineFee?.toString() || '',
          offlineFee: doctor.offlineFee?.toString() || '',
          availableOnline: doctor.availableOnline ?? false,
          availableOffline: doctor.availableOffline ?? false,
          clinic: doctor.clinic || '',
          address: doctor.address || '',
          latitude: doctor.latitude?.toString() || '',
          longitude: doctor.longitude?.toString() || '',
          hospitalId: doctor.hospitalId || '',
        }
      : EMPTY_FORM
  );
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [shake, setShake] = useState(false);
  const fieldRefs = useRef({});

  const set = (key) => (e) => {
    setForm(f => ({ ...f, [key]: e.target.value }));
    if (errors[key]) setErrors(e2 => ({ ...e2, [key]: null }));
  };

  const toggleLanguage = (lang) => {
    setForm(f => ({
      ...f,
      languages: f.languages.includes(lang)
        ? f.languages.filter(l => l !== lang)
        : [...f.languages, lang],
    }));
  };

const validate = () => {
  const errs = {};

  // Name
  if (!form.name.trim()) {
    errs.name = 'Name is required';
  }

  // Phone Number
  const phone = form.phoneNumber.replace(/[\s-]/g, ''); // Remove spaces & hyphens

  if (!phone) {
    errs.phoneNumber = 'Phone number is required';
  } else if (!/^(\+91)?[6-9]\d{9}$/.test(phone)) {
    errs.phoneNumber =
      'Enter a valid Indian mobile number (10 digits starting with 6, 7, 8 or 9)';
  }

  // Department
  if (!form.department.trim()) {
    errs.department = 'Department is required';
  }

  // Qualifications
  if (!form.qualifications.trim()) {
    errs.qualifications = 'Qualifications are required';
  }

  // Experience
  const experience = Number(form.experience);

  if (form.experience === '') {
    errs.experience = 'Experience is required';
  } else if (!Number.isInteger(experience) || experience < 0 || experience > 70) {
    errs.experience = 'Experience must be between 0 and 70 years';
  }

  // Email
  if (!form.email.trim()) {
    errs.email = 'Email is required';
  } else if (
    !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(form.email)
  ) {
    errs.email = 'Enter a valid email address';
  }

  // Languages
  if (!form.languages.length) {
    errs.languages = 'Select at least one language';
  }

  // Online Fee
  if (form.availableOnline) {
    const fee = Number(form.onlineFee);

    if (form.onlineFee === '') {
      errs.onlineFee = 'Online consultation fee is required';
    } else if (isNaN(fee) || fee < 0) {
      errs.onlineFee = 'Enter a valid online consultation fee';
    }
  }

  // Offline Fee
  if (form.availableOffline) {
    const fee = Number(form.offlineFee);

    if (form.offlineFee === '') {
      errs.offlineFee = 'Offline consultation fee is required';
    } else if (isNaN(fee) || fee < 0) {
      errs.offlineFee = 'Enter a valid offline consultation fee';
    }
  }

  // Clinic
  if (!form.clinic.trim()) {
    errs.clinic = 'Clinic / Hospital name is required';
  }

  // Address
  if (!form.address.trim()) {
    errs.address = 'Address is required';
  }

  return errs;
};

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      // Make the failure impossible to miss, regardless of scroll position:
      // jump to the first invalid field (in form order) and give the modal a shake.
      const firstBadField = FIELD_ORDER.find(key => errs[key]);
      const el = fieldRefs.current[firstBadField];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = el.querySelector('input, select, textarea');
        if (input) input.focus({ preventScroll: true });
      }
      setShake(true);
      setTimeout(() => setShake(false), 450);
      return;
    }
    setSaving(true);
    const payload = {
      phoneNumber: form.phoneNumber.trim(),
      name: form.name.trim(),
      department: form.department,
      qualifications: form.qualifications.trim(),
      experience: parseInt(form.experience, 10),
      email: form.email.trim(),
      photoUrl: form.photoUrl.trim() || null,
      languages: form.languages,
      about: form.about.trim() || null,
      onlineFee: parseFloat(form.onlineFee),
      offlineFee: parseFloat(form.offlineFee),
      availableOnline: form.availableOnline,
      availableOffline: form.availableOffline,
      clinic: form.clinic.trim(),
      address: form.address.trim(),
      latitude: form.latitude !== '' ? parseFloat(form.latitude) : null,
      longitude: form.longitude !== '' ? parseFloat(form.longitude) : null,
      hospitalId: form.hospitalId || null,
    };
    await onSave(payload);
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 20, width: '100%', maxWidth: 620,
        maxHeight: '90vh', overflowY: 'auto', padding: '28px 28px 24px',
        boxShadow: '0 24px 64px rgba(79,70,229,0.18)',
        animation: shake ? 'shake .45s ease' : 'fadeUp .25s ease both',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22 }}>
          <div>
            <h2 className="modal-title">{isEdit ? 'Edit doctor' : 'Create doctor account'}</h2>
            <p className="modal-sub">
              {isEdit ? 'Update this doctor\'s profile' : 'The phone number becomes their OTP login credential'}
            </p>
          </div>
          <button onClick={onClose} className="btn-close-x" style={{ fontSize: 22 }}>×</button>
        </div>

        {Object.keys(errors).length > 0 && (
          <div style={{
            position: 'sticky', top: 0, zIndex: 5,
            margin: '0 0 18px', padding: '10px 16px', borderRadius: 10,
            background: '#fcebeb', border: '1px solid #f09595',
            color: '#a32d2d', fontSize: 12.5, fontWeight: 600,
            fontFamily: 'DM Sans, sans-serif',
            display: 'flex', alignItems: 'center', gap: 8,
            boxShadow: '0 4px 14px rgba(220,38,38,0.12)',
          }}>
            <span>⚠️</span>
            <span>
              Please fix {Object.keys(errors).length} field{Object.keys(errors).length > 1 ? 's' : ''}:{' '}
              {Object.keys(errors).map(k => FIELD_LABELS[k] || k).join(', ')}
            </span>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Phone + Name */}
          <div className="grid-2col">
            <div className="field" style={{ marginBottom: 0 }} ref={el => (fieldRefs.current.phoneNumber = el)}>
              <label className="t-label">Phone number <span className="required-star">*</span></label>
              <input
                value={form.phoneNumber}
                onChange={set('phoneNumber')}
                className={`input${errors.phoneNumber ? ' err' : ''}`}
                placeholder="+919876543210"
              />
              {errors.phoneNumber && <div className="field-error">{errors.phoneNumber}</div>}
              {isEdit && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>Changing this updates their OTP login number</div>}
            </div>
            <div className="field" style={{ marginBottom: 0 }} ref={el => (fieldRefs.current.name = el)}>
              <label className="t-label">Name <span className="required-star">*</span></label>
              <input
                value={form.name}
                onChange={set('name')}
                className={`input${errors.name ? ' err' : ''}`}
                placeholder="e.g. Dr. Priya Sharma"
              />
              {errors.name && <div className="field-error">{errors.name}</div>}
            </div>
          </div>

          {/* Department + Qualifications */}
          <div className="grid-2col">
            <div
  className="field"
  style={{ marginBottom: 0 }}
  ref={el => (fieldRefs.current.department = el)}
>
  <label className="t-label">
    Department <span className="required-star">*</span>
  </label>

  <input
    value={form.department}
    onChange={set('department')}
    className={`input${errors.department ? ' err' : ''}`}
    placeholder="Type or select department…"
    list="department-options"
    autoComplete="off"
  />

  <datalist id="department-options">
    {departments
      .filter(d => d.id !== 'all')
      .map(d => (
        <option key={d.id} value={d.label} />
      ))}
  </datalist>

  {errors.department && (
    <div className="field-error">{errors.department}</div>
  )}
</div>
            <div className="field" style={{ marginBottom: 0 }} ref={el => (fieldRefs.current.qualifications = el)}>
              <label className="t-label">Qualifications <span className="required-star">*</span></label>
              <input
                value={form.qualifications}
                onChange={set('qualifications')}
                className={`input${errors.qualifications ? ' err' : ''}`}
                placeholder="e.g. MBBS, MD (Cardiology)"
              />
              {errors.qualifications && <div className="field-error">{errors.qualifications}</div>}
            </div>
          </div>

          {/* Experience + Email */}
          <div className="grid-2col">
            <div className="field" style={{ marginBottom: 0 }} ref={el => (fieldRefs.current.experience = el)}>
              <label className="t-label">Experience (years) <span className="required-star">*</span></label>
              <input
                type="number" min="0" step="1"
                value={form.experience}
                onChange={set('experience')}
                className={`input${errors.experience ? ' err' : ''}`}
                placeholder="e.g. 12"
              />
              {errors.experience && <div className="field-error">{errors.experience}</div>}
            </div>
            <div className="field" style={{ marginBottom: 0 }} ref={el => (fieldRefs.current.email = el)}>
              <label className="t-label">Email <span className="required-star">*</span></label>
              <input type="email" value={form.email} onChange={set('email')} className={`input${errors.email ? ' err' : ''}`} placeholder="doctor@hospital.com" />
              {errors.email && <div className="field-error">{errors.email}</div>}
            </div>
          </div>

          {/* Photo URL */}
          <div className="field" style={{ marginBottom: 0 }}>
            <label className="t-label">Photo URL</label>
            <input value={form.photoUrl} onChange={set('photoUrl')} className="input" placeholder="https://…" />
          </div>

          {/* Languages */}
          <div className="field" style={{ marginBottom: 0 }} ref={el => (fieldRefs.current.languages = el)}>
            <label className="t-label">Languages <span className="required-star">*</span></label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
              {LANGUAGE_OPTIONS.map(lang => {
                const active = form.languages.includes(lang);
                return (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => toggleLanguage(lang)}
                    style={{
                      padding: '5px 13px', borderRadius: 9999, fontSize: 12.5, fontWeight: 600,
                      cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
                      border: active ? '1.5px solid #c7d2fe' : '1.5px solid #e5e7eb',
                      background: active ? '#eef2ff' : '#fff',
                      color: active ? '#4f46e5' : '#9ca3af',
                    }}
                  >
                    {lang}
                  </button>
                );
              })}
            </div>
            {errors.languages && <div className="field-error">{errors.languages}</div>}
          </div>

          {/* About */}
          <div className="field" style={{ marginBottom: 0 }}>
            <label className="t-label">About</label>
            <textarea
              value={form.about}
              onChange={set('about')}
              className="input"
              rows={2}
              placeholder="Brief bio / description…"
              style={{ resize: 'vertical', lineHeight: 1.5 }}
            />
          </div>
          
          {/* Availability toggles */}
          <div style={{ display: 'flex', gap: 24, paddingTop: 4 }}>
            
            <TogglePill
              checked={form.availableOnline}
              onChange={(v) => setForm(f => ({ ...f, availableOnline: v }))}
              label="Available online"
            />
            <TogglePill
              checked={form.availableOffline}
              onChange={(v) => setForm(f => ({ ...f, availableOffline: v }))}
              label="Available offline"
              color="#059669" bg="#ecfdf5"
            />
          </div>
          {/* Fees */}
          <div className="grid-2col">
            <div className="field" style={{ marginBottom: 0 }} ref={el => (fieldRefs.current.onlineFee = el)}>
              <label className="t-label">Online fee (₹)</label>
              <input disabled={form.availableOnline === false} type="number" step="0.01" min="0" value={form.onlineFee} onChange={set('onlineFee')} className={`input${errors.onlineFee ? ' err' : ''}`} placeholder="500"/>
              {errors.onlineFee && <div className="field-error">{errors.onlineFee}</div>}
            </div>
            <div className="field" style={{ marginBottom: 0 }} ref={el => (fieldRefs.current.offlineFee = el)}>
              <label className="t-label">Offline fee (₹)</label>
              <input disabled={form.availableOffline === false} type="number" step="0.01" min="0" value={form.offlineFee} onChange={set('offlineFee')} className={`input${errors.offlineFee ? ' err' : ''}`} placeholder="800"/>
              {errors.offlineFee && <div className="field-error">{errors.offlineFee}</div>}
            </div>
          </div>

          

          {/* Clinic + Address */}
          <div className="grid-2col">
            <div className="field" style={{ marginBottom: 0 }} ref={el => (fieldRefs.current.clinic = el)}>
              <label className="t-label">Clinic / hospital name <span className="required-star">*</span></label>
              <input value={form.clinic} onChange={set('clinic')} className={`input${errors.clinic ? ' err' : ''}`} placeholder="e.g. S4 Heart Centre" />
              {errors.clinic && <div className="field-error">{errors.clinic}</div>}
            </div>
            <div className="field" style={{ marginBottom: 0 }} ref={el => (fieldRefs.current.address = el)}>
              <label className="t-label">Address <span className="required-star">*</span></label>
              <input value={form.address} onChange={set('address')} className={`input${errors.address ? ' err' : ''}`} placeholder="e.g. Jubilee Hills, Hyderabad" />
              {errors.address && <div className="field-error">{errors.address}</div>}
            </div>
          </div>

          {/* Lat/Lng */}
          <div className="grid-2col">
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="t-label">Latitude</label>
              <input type="number" step="any" value={form.latitude} onChange={set('latitude')} className="input" placeholder="17.4239" />
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="t-label">Longitude</label>
              <input type="number" step="any" value={form.longitude} onChange={set('longitude')} className="input" placeholder="78.4483" />
            </div>
          </div>

          {/* Hospital link */}
          <div className="field" style={{ marginBottom: 0 }}>
            <label className="t-label">Linked hospital</label>
            <select value={form.hospitalId} onChange={set('hospitalId')} className="input">
              <option value="">None</option>
              {hospitals.map(h => (
                <option key={h.id} value={h.id}>{h.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Actions */}
        <div className="modal-actions" style={{ marginTop: 22 }}>
          <button onClick={onClose} className="btn-outline" style={{ flex: 1, color: '#6b7280' }}>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            style={{
              flex: 2, padding: '12px', fontSize: 14, fontWeight: 600,
              borderRadius: 10, cursor: saving ? 'not-allowed' : 'pointer',
              border: 'none', background: saving ? '#c7d2fe' : 'linear-gradient(135deg, #4f46e5, #7c3aed)',
              color: '#fff', opacity: saving ? 0.8 : 1, transition: 'opacity .15s',
              fontFamily: 'DM Sans, sans-serif',
            }}>
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create doctor'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
const AdminDoctors = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { tenant } = useTenant();

  const [doctors, setDoctors]       = useState([]);
  const [departments, setDepartments] = useState([]);
  const [hospitals, setHospitals]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [search, setSearch]         = useState('');

  const [editDoctor, setEditDoctor]     = useState(null); // doctor or {} for new
  const [actionTarget, setActionTarget] = useState(null); // { doctor, action: 'deactivate' | 'activate' }
  const [actionLoading, setActionLoading] = useState(false);

  const [toast, setToast] = useState({ visible: false, message: '', type: 'success' });
  const showToast = useCallback((message, type = 'success') => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast(t => ({ ...t, visible: false })), 3200);
  }, []);

  const load = useCallback(async () => {
    if (!token || !tenant?.id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getAllDoctorsAdmin(token, tenant.id);
      setDoctors(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token, tenant?.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    getDepartments().then(setDepartments).catch(() => setDepartments([]));
    api.get('/consultations/hospitals')
      .then(({ data }) => setHospitals(Array.isArray(data) ? data : []))
      .catch(() => setHospitals([]));
  }, []);

  const handleSave = async (payload) => {
    try {
      if (editDoctor?.id) {
        await updateDoctorAdmin(token, tenant.id, editDoctor.id, payload);
        showToast('Doctor updated');
      } else {
        await createDoctorAdmin(token, tenant.id, payload);
        showToast(`Doctor account created. They can now log in with ${payload.phoneNumber} via OTP.`);
      }
      setEditDoctor(null);
      load();
    } catch (e) {
      showToast(`Save failed: ${e.message}`, 'error');
    }
  };

  const handleConfirmAction = async () => {
    if (!actionTarget) return;
    const { doctor, action } = actionTarget;
    setActionLoading(true);
    try {
      if (action === 'deactivate') {
        await deactivateDoctorAdmin(token, tenant.id, doctor.id);
        showToast('Doctor deactivated');
      } else {
        await reactivateDoctorAdmin(token, tenant.id, doctor.id);
        showToast('Doctor reactivated');
      }
      setActionTarget(null);
      load();
    } catch (e) {
      showToast(`${action === 'deactivate' ? 'Deactivation' : 'Reactivation'} failed: ${e.message}`, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const filtered = doctors.filter(d => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (d.name || '').toLowerCase().includes(q) ||
      (d.department || '').toLowerCase().includes(q) ||
      (d.phoneNumber || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="page">
      <main className="page-main">

        <div className="breadcrumb">
          <button className="t-link" onClick={() => navigate('/')}>Home</button>
          <span>›</span>
          <span className="bc-current">Manage Doctors</span>
        </div>

        <div className="fade-up" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <h1 className="t-h1" style={{ marginBottom: 4 }}>👨‍⚕️ Manage Doctors</h1>
            <p className="t-body-sm">
              {!loading && !error && `${doctors.length} doctor${doctors.length !== 1 ? 's' : ''} total`}
            </p>
          </div>
          <button
            onClick={() => setEditDoctor({})}
            style={{
              fontSize: 13.5, fontWeight: 700, color: '#fff',
              background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
              border: 'none', borderRadius: 12, padding: '10px 20px',
              cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
              boxShadow: '0 4px 14px rgba(79,70,229,0.25)',
            }}
          >
            + Add Doctor
          </button>
        </div>

        <div className="card fade-up-1" style={{ marginBottom: '1.25rem', padding: '14px 18px' }}>
          <div className="search-wrap" style={{ marginBottom: 0 }}>
            <svg className="search-icon" width="16" height="16" fill="none" stroke="#9ca3af" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35" strokeLinecap="round"/>
            </svg>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="search-input"
              placeholder="Search by name, department, phone…"
              style={{ height: 36, fontSize: 13 }}
            />
            {search && <button className="search-clear" onClick={() => setSearch('')} style={{ fontSize: 16 }}>×</button>}
          </div>
        </div>

        {error && (
          <div className="error-state" style={{ marginBottom: '1rem' }}>
            <span>⚠️</span> {error}
            <button className="t-link" style={{ marginLeft: 12 }} onClick={load}>Retry</button>
          </div>
        )}

        <div className="card fade-up-2" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', padding: '10px 20px',
            background: '#f8f9ff', borderBottom: '1px solid #f3f4f6',
            fontSize: 11, fontWeight: 600, color: '#9ca3af',
            textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'DM Sans, sans-serif',
          }}>
            <span>Doctor</span>
            <span>Actions</span>
          </div>

          <div style={{ padding: '0 20px' }}>
            {loading
              ? Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} style={{ display: 'flex', gap: 14, padding: '14px 0', borderBottom: '1px solid #f3f4f6', alignItems: 'center' }}>
                    <div className="skeleton" style={{ width: 40, height: 40, borderRadius: '50%', flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div className="skeleton skeleton-line" style={{ width: '35%', height: 13, marginBottom: 6 }} />
                      <div className="skeleton skeleton-line" style={{ width: '50%', height: 11 }} />
                    </div>
                  </div>
                ))
              : filtered.length === 0
                ? (
                  <div className="empty-state">
                    <div className="empty-icon">👨‍⚕️</div>
                    <p className="empty-title">{search ? 'No doctors match' : 'No doctors yet'}</p>
                  </div>
                )
                : filtered.map((d, idx) => {
                  const isInactive = d.active === false;
                  return (
                  <div key={d.id} style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    padding: '14px 0', borderBottom: idx < filtered.length - 1 ? '1px solid #f3f4f6' : 'none',
                    animation: `fadeUp .3s ${idx * 20}ms ease both`,
                  }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                      background: 'linear-gradient(135deg, #7c3aedDD, #7c3aed88)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 13, fontWeight: 700, color: '#fff', fontFamily: 'Sora, sans-serif',
                    }}>
                      {(d.name || '?').split(' ').slice(0, 2).map(w => w[0]?.toUpperCase()).join('')}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700, fontSize: 13.5, color: '#1e1b4b', marginBottom: 2 }}>
                        {d.name}
                        {isInactive && (
                          <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: '#dc2626', background: '#fef2f2', padding: '1px 8px', borderRadius: 9999 }}>
                            Deactivated
                          </span>
                        )}
                      </div>
                      <div className="t-meta">
                        <span>{d.department}</span>
                        <span style={{ marginLeft: 10 }}>📱 {d.phoneNumber}</span>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                      <button
                        onClick={() => setEditDoctor(d)}
                        style={{
                          fontSize: 12, fontWeight: 600, color: '#4f46e5', background: '#eef2ff',
                          border: '1px solid #c7d2fe', borderRadius: 8, padding: '6px 12px', cursor: 'pointer',
                          fontFamily: 'DM Sans, sans-serif',
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setActionTarget({ doctor: d, action: isInactive ? 'activate' : 'deactivate' })}
                        style={{
                          fontSize: 12, fontWeight: 600,
                          color: isInactive ? '#059669' : '#dc2626',
                          background: isInactive ? '#ecfdf5' : '#fef2f2',
                          border: isInactive ? '1px solid #a7f3d0' : '1px solid #fecaca',
                          borderRadius: 8, padding: '6px 12px', cursor: 'pointer',
                          fontFamily: 'DM Sans, sans-serif',
                        }}
                      >
                        {isInactive ? 'Activate' : 'Deactivate'}
                      </button>
                    </div>
                  </div>
                  );
                })
            }
          </div>
        </div>

      </main>

      {editDoctor && (
        <DoctorModal
          doctor={editDoctor}
          departments={departments}
          hospitals={hospitals}
          onSave={handleSave}
          onClose={() => setEditDoctor(null)}
        />
      )}

      {actionTarget && (
        <ConfirmModal
          doctor={actionTarget.doctor}
          action={actionTarget.action}
          onConfirm={handleConfirmAction}
          onCancel={() => setActionTarget(null)}
          loading={actionLoading}
        />
      )}

      <Toast {...toast} />
    </div>
  );
};

export default AdminDoctors;